import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, Checkbox, FormControlLabel, IconButton, Tooltip, Typography } from '@mui/material';

import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerEnrollmentStatus, PayerRow } from './types';

export interface IntakePayerTableProps {
  canManage?: boolean;
  derivedSentence?: string | null;
  help?: ReactNode;
  loading?: boolean;
  onEnrollmentStatusChange?: (rowId: string, status: PayerEnrollmentStatus) => void;
  onRemove?: (rowId: string) => void;
  onServiceChange?: (rowId: string, service: 'jotcred' | 'jotbill', enabled: boolean) => void;
  rows: PayerRow[];
  search?: ReactNode;
}

export function IntakePayerTable({
  canManage = false,
  derivedSentence,
  help,
  loading = false,
  onEnrollmentStatusChange,
  onRemove,
  onServiceChange,
  rows,
  search,
}: IntakePayerTableProps) {
  const renderSection = (status: PayerEnrollmentStatus, heading: string, emptyMessage: string) => {
    const sectionRows = rows.filter(row => row.enrollmentStatus === status);
    const otherStatus: PayerEnrollmentStatus =
      status === 'already_enrolled' ? 'wants_to_enroll' : 'already_enrolled';
    const otherLabel = status === 'already_enrolled' ? 'Want to enroll' : 'Already enrolled';

    return (
      <Box component="section" aria-labelledby={`payer-roster-${status}-heading`} sx={{ mb: 2 }}>
        <Typography
          component="h3"
          id={`payer-roster-${status}-heading`}
          sx={{ color: wf.green, fontSize: '10.5px', fontWeight: 700, mb: 0.25 }}
        >
          {heading}
        </Typography>
        {sectionRows.length === 0 ? (
          <Typography sx={{ color: wf.muted, fontSize: '12px', py: 1 }}>{emptyMessage}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              aria-labelledby={`payer-roster-${status}-heading`}
              sx={{
                width: '100%',
                minWidth: 620,
                borderCollapse: 'collapse',
                fontSize: '12.5px',
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  {[
                    ['Insurance company', '48%'],
                    ['Payer ID', '18%'],
                    ['JotPsych services', '34%'],
                  ].map(([columnHeading, width]) => (
                    <Box
                      component="th"
                      key={columnHeading}
                      scope="col"
                      sx={{
                        borderBottom: `1px solid ${wf.line}`,
                        color: wf.muted,
                        fontSize: '10px',
                        letterSpacing: '.06em',
                        p: '6px 8px',
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        width,
                      }}
                    >
                      {columnHeading}
                    </Box>
                  ))}
                  {canManage && <Box component="th" aria-label="Actions" />}
                </Box>
              </Box>
              <Box component="tbody">
                {sectionRows.map(row => (
                  <Box component="tr" key={row.rowId}>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '7px 8px' }}
                    >
                      {row.displayName}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        borderBottom: `1px solid ${wf.lineSoft}`,
                        color: wf.muted,
                        p: '7px 8px',
                      }}
                    >
                      {row.payerId}
                    </Box>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '2px 8px' }}
                    >
                      {(['jotcred', 'jotbill'] as const).map(service => (
                        <FormControlLabel
                          key={service}
                          control={
                            <Checkbox
                              checked={row[service]}
                              disabled={!canManage}
                              onChange={event =>
                                onServiceChange?.(row.rowId, service, event.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={service === 'jotcred' ? 'JotCred' : 'JotBill'}
                          sx={{
                            '& .MuiFormControlLabel-label': { fontSize: '12px' },
                            mr: 1,
                          }}
                        />
                      ))}
                    </Box>
                    {canManage && (
                      <Box
                        component="td"
                        sx={{
                          borderBottom: `1px solid ${wf.lineSoft}`,
                          p: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={`Move to ${otherLabel}`}>
                          <IconButton
                            aria-label={`Move ${row.displayName} to ${otherLabel}`}
                            onClick={() => onEnrollmentStatusChange?.(row.rowId, otherStatus)}
                            size="small"
                            sx={{ color: wf.faint }}
                          >
                            <SwapVertIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          aria-label={`Remove ${row.displayName}`}
                          onClick={() => onRemove?.(row.rowId)}
                          size="small"
                          sx={{ color: wf.faint }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ ...cardSx, gridColumn: '1 / -1' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
        <Box sx={groupTitleSx}>Your payer list</Box>
        {help}
        <Box sx={chipSx('mut')}>
          {canManage ? 'staff can edit' : 'managed by your JotPsych team'}
        </Box>
      </Box>

      {canManage && search && <Box sx={{ maxWidth: 480, mb: 1.5 }}>{search}</Box>}

      {loading ? (
        <Typography role="status" sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          Loading payer list…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          {canManage
            ? 'No payers configured. Search above to add the first payer.'
            : 'No payers configured. Please contact your JotPsych representative to add payers.'}
        </Typography>
      ) : (
        <>
          {renderSection(
            'already_enrolled',
            'Already enrolled',
            'No payers are marked as already enrolled.',
          )}
          {renderSection(
            'wants_to_enroll',
            'Want to enroll',
            'No payers are waiting for enrollment.',
          )}
        </>
      )}

      <Typography sx={{ color: wf.muted, fontSize: '11.5px', mt: 1.25 }}>
        Provider enrollment status is tracked separately for each payer and service.
      </Typography>

      {derivedSentence && (
        <Box
          sx={{
            mt: 1,
            fontSize: '11.5px',
            color: wf.amberWarn,
            bgcolor: wf.amberWarnSoft,
            borderRadius: '6px',
            p: '8px 10px',
          }}
        >
          {derivedSentence}
        </Box>
      )}
    </Box>
  );
}
